/**
 * Configuración de entorno centralizada para la aplicación
 * Este archivo contiene la configuración específica para cada entorno
 * y funciones para detectar automáticamente el entorno actual
 */

/**
 * Tipos de entorno soportados por la aplicación
 * 
 * Se alinean con los modos de ejecución del SDK definidos en el enum ENV:
 * - local (Development): Modo exclusivo para desarrollo interno
 * - staging (Testing): Modo de pruebas para los integradores
 * - production (Production): Modo de producción para uso en entornos productivos
 */
export type Environment = 'local' | 'staging' | 'production';

/**
 * Interfaz para la configuración específica de cada entorno
 */
export interface EnvironmentConfig {
  // API
  apiBaseUrl: string;

  // SDK
  sdkUrl: string;
  sdkMode: 'development' | 'testing' | 'production';

  // Link Pago base URL (for generated payment links)
  linkBaseUrl: string;

  // Valid domains for this environment (for domain detection)
  validDomains: string[];

  // Otros parámetros
  debug: boolean;
  defaultHotelSlug?: string;
}

/**
 * Detecta el entorno actual basado en el hostname
 */
export function getCurrentEnvironment(): Environment {
  // Solo ejecutar en el navegador
  if (typeof window === 'undefined') {
    return 'local'; // Por defecto en SSR o cuando no hay window
  }

  const hostname = window.location.hostname;

  // Local development domains
  if (hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.test')) {
    return 'local';
  }

  // Staging domains (Yopresto)
  if (hostname.includes('-staging.yopresto.net') ||
      hostname === 'link-staging.yopresto.net') {
    return 'staging';
  }

  // Production domains (Yopresto)
  if (hostname.includes('.yopresto.net') ||
      hostname === 'link.yopresto.net') {
    return 'production';
  }

  // Default to production for unknown domains
  return 'production';
}

/**
 * Configuración para cada entorno
 * 
 * Basado en la tabla de configuración del SDK en CONTRIBUTING.md:
 * https://github.com/Hotelpay/sdk/blob/main/CONTRIBUTING.md
 */
const configs: Record<Environment, EnvironmentConfig> = {
  local: {
    // API Base: Desarrollo
    apiBaseUrl: 'https://backend.test/api',

    // SDK: Desarrollo (ENV.Development)
    // Loads compiled UMD bundle from local filesystem
    sdkUrl: 'https://link.test/hotelpay.umd.js',
    sdkMode: 'development',

    // Link Pago base URL
    linkBaseUrl: 'https://link.test',

    // Valid domains for local development
    validDomains: ['link.test', 'localhost'],

    // Otros parámetros
    debug: true,
    defaultHotelSlug: 'hotel-zafiro'
  },
  staging: {
    // API Base: Testing (Yopresto)
    apiBaseUrl: 'https://testing.yopresto.net/api',

    // SDK: Testing (ENV.Testing) - Yopresto CDN
    sdkUrl: 'https://sdk-staging.yopresto.net/v1/yopresto.js',
    sdkMode: 'testing',

    // Link Pago base URL (Yopresto staging)
    linkBaseUrl: 'https://link-staging.yopresto.net',

    // Valid domains for staging
    validDomains: ['link-staging.yopresto.net'],

    // Otros parámetros
    debug: true
  },
  production: {
    // API Base: Producción (Yopresto)
    apiBaseUrl: 'https://admin.yopresto.net/api',

    // SDK: Producción (ENV.Production) - Yopresto CDN
    sdkUrl: 'https://sdk.yopresto.net/v1/yopresto.js',
    sdkMode: 'production',

    // Link Pago base URL (Yopresto production)
    linkBaseUrl: 'https://link.yopresto.net',

    // Valid domains for production
    validDomains: ['link.yopresto.net'],

    // Otros parámetros
    debug: false
  }
};

/**
 * Obtiene la configuración para el entorno actual
 *
 * Nota: Esta función incluye soporte para sobreescritura de entorno vía localStorage.
 * Para usarlo en la consola del navegador:
 *
 * // Para usar config de staging en cualquier entorno
 * localStorage.setItem('env_override', 'staging');
 *
 * // Para usar config de producción
 * localStorage.setItem('env_override', 'production');
 *
 * // Para eliminar la sobreescritura
 * localStorage.removeItem('env_override');
 *
 * // Recargar la página después de cambiar
 * location.reload();
 */
export function getConfig(): EnvironmentConfig {
  // Verificar si hay una sobreescritura en localStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    const overrideEnv = window.localStorage.getItem('env_override');
    if (overrideEnv && configs[overrideEnv as Environment]) {
      console.log(`🔄 Usando configuración de entorno sobreescrita: ${overrideEnv}`);
      return configs[overrideEnv as Environment];
    }
  }

  const env = getCurrentEnvironment();
  return configs[env];
}

// Exporta la configuración directamente para uso conveniente
export default getConfig();